import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Step, StepLabel, Stepper as MUIStepper, StepperProps, StepConnector } from '@mui/material';
import React, { Fragment } from 'react';

interface Props {
  activeStep: StepperProps['activeStep'];
  steps: Array<string>;
  skippedSteps?: Set<number>;
  completedSteps?: Set<number>;
  labelOrientation?: 'horizontal' | 'vertical';
  onStepClick?: (step: number) => void;
  skippedStepIcon?: React.ReactNode;
}

export default function Stepper({
  steps,
  activeStep,
  skippedSteps,
  completedSteps,
  labelOrientation = 'vertical',
  onStepClick,
  skippedStepIcon,
}: Props) {
  const isStepSkipped = (step: number) => {
    return skippedSteps?.has(step);
  };

  const isCompleted = (step: number) =>
    !isStepSkipped(step) && (!!completedSteps?.has(step) || (!!activeStep && activeStep > step));

  const isSkippedAsCompleted = (step: number) =>
    step !== activeStep && isStepSkipped(step) && skippedStepIcon != null;

  const looksCompleted = (step: number) => isCompleted(step) || isSkippedAsCompleted(step);

  const renderStepIcon = (index: number) => {
    if (isSkippedAsCompleted(index)) return <>{skippedStepIcon}</>;
    return isCompleted(index) ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />;
  };

  return (
    <MUIStepper
      activeStep={activeStep}
      alternativeLabel={labelOrientation === 'vertical'}
      connector={null}
    >
      {steps.map((label, index) => {
        const stepProps: { completed?: boolean } = {};
        const labelProps: { optional?: React.ReactNode } = {};

        if (isStepSkipped(index)) {
          stepProps.completed = isSkippedAsCompleted(index);
        }

        return (
          <Fragment key={label}>
            {index !== 0 && index < steps.length && (
              <StepConnector
                className={`${activeStep === index ? 'Mui-active' : ''} ${
                  looksCompleted(index) ? 'Mui-completed' : ''
                }`}
              />
            )}
            <Step key={label} {...stepProps}>
              <StepLabel
                {...labelProps}
                onClick={onStepClick ? () => onStepClick(index) : undefined}
                sx={{
                  '& .MuiStepLabel-label': { marginTop: 0 },
                  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': { marginTop: 0 },
                  ...(onStepClick && { cursor: 'pointer' }),
                }}
                slots={{
                  stepIcon: () => renderStepIcon(index),
                }}
                className={`${activeStep === index ? 'Mui-active' : ''} ${
                  looksCompleted(index) ? 'Mui-completed' : ''
                }`}
              >
                {label}
              </StepLabel>
            </Step>
          </Fragment>
        );
      })}
    </MUIStepper>
  );
}
